import CalculateEMI from "./CalculateEMI";
import Input from "../../components/composed/FormElements/Input";

type DeviationValidation = "ROI" | "MONTHS" | "PROCESSINGFEE";

type DeviationFieldConfig = {
  originalValue: string | number;
  proposedValue?: string | number;
  label: string;
  validation: DeviationValidation;
  isDisabled?: boolean;
};

type FieldsConfig = Record<string, DeviationFieldConfig>;

type Validator = (v: unknown) => true | string;

type RegisterOptionsLite = {
  valueAsNumber?: boolean;
  validate?: Record<string, Validator>;
};

type RegisterFn = (name: string, options?: RegisterOptionsLite) => unknown;
type GetValuesFn = (path?: string) => unknown;

type ErrorsType = {
  proposed?: Record<string, { message?: string }>;
};

interface IntiateFormProps {
  fields: FieldsConfig;
  errors: ErrorsType;
  isValid: boolean;
  register: RegisterFn;
  textArea: { name: string; label: string };
  loanOffer: unknown;
  getValues: GetValuesFn;
  isMFL_LAP?: boolean;
  isABFLBL: boolean;
  isABFLPL: boolean;
}

// Helpers to avoid repeated null/empty checks and to keep proposals optional
const notProvided = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  v === "" ||
  (typeof v === "number" && Number.isNaN(v));

const isNumberValid =
  (msg: string): Validator =>
  (v: unknown) =>
    notProvided(v) || (typeof v === "number" && !Number.isNaN(v)) || msg;

const isIntegerValid =
  (msg: string): Validator =>
  (v: unknown) =>
    notProvided(v) || Number.isInteger(v as number) || msg;

const isGreaterThanZero =
  (msg: string): Validator =>
  (v: unknown) =>
    notProvided(v) || Number(v as number) > 0 || msg;

const requiredField =
  (msg: string): Validator =>
  (v: unknown) =>
    !notProvided(v) || msg;

// Allow up to 2 decimal places (for ROI and Processing Fee)
const maxTwoDecimals =
  (msg: string): Validator =>
  (v: unknown) => {
    if (notProvided(v)) return true;
    const num = v as number;
    // Use an epsilon to avoid floating precision issues
    return Math.abs(num * 100 - Math.round(num * 100)) < 1e-9 || msg;
  };

const VALIDATION_META: Record<
  DeviationValidation,
  {
    label: string;
    requireInteger?: boolean;
    maxTwoDecimals?: boolean;
  }
> = {
  ROI: { label: "Interest rate", maxTwoDecimals: true },
  MONTHS: { label: "Tenure", requireInteger: true },
  PROCESSINGFEE: { label: "Processing Fee", maxTwoDecimals: true }
};

const createNumericValidators = (
  label: string,
  opts: { requireInteger?: boolean; maxTwoDecimals?: boolean } = {}
): Record<string, Validator> => {
  const rules: Record<string, Validator> = {
    required: requiredField("Field cannot be empty"),
    isNumber: isNumberValid(`${label} must be a number`),
    greaterThanZero: isGreaterThanZero(`${label} must be greater than 0`)
  };
  if (opts.requireInteger)
    rules.isInteger = isIntegerValid(`${label} must be a whole number`);
  if (opts.maxTwoDecimals)
    rules.maxTwoDecimals = maxTwoDecimals(`${label} can have up to 2 decimals`);

  return rules;
};

const buildValidators = (
  type: DeviationValidation
): RegisterOptionsLite | object => {
  const meta = VALIDATION_META[type];
  if (!meta) return {};

  return {
    valueAsNumber: true,
    validate: createNumericValidators(meta.label, {
      requireInteger: meta.requireInteger,
      maxTwoDecimals: meta.maxTwoDecimals
    })
  };
};

const getStepFromValidation = (type: DeviationValidation): string =>
  VALIDATION_META[type]?.maxTwoDecimals ? "0.01" : "1";

export default function IntiateForm({
  fields,
  errors,
  isValid,
  register,
  textArea,
  loanOffer,
  getValues,
  isMFL_LAP,
  isABFLBL,
  isABFLPL
}: IntiateFormProps) {
  const getErrorText = (fieldKey: string): string => {
    const proposedErrors = errors?.proposed ?? {};

    return proposedErrors?.[fieldKey]?.message || "";
  };

  return (
    <>
      {Object?.keys(fields || {})?.length && (
        <div>
          {Object?.keys(fields)?.map((field) => {
            return (
              <div
                className="grid grid-cols-2 gap-5 mb-5 last:mb-0"
                key={field}
              >
                <fieldset className="">
                  <Input
                    type="text"
                    disabled={true}
                    name={`original.${field}`}
                    label={`Original ${fields[field]?.label}`}
                    {...(isABFLBL
                      ? {
                          register: register(`original.${field}`, {
                            value: fields?.[field]?.originalValue ?? ""
                          })
                        }
                      : isABFLPL
                        ? {
                            register: register(`original.${field}`)
                          }
                        : {})}
                  />
                </fieldset>

                <fieldset className="">
                  <Input
                    step={getStepFromValidation(fields?.[field]?.validation)}
                    type="number"
                    name={`proposed.${field}`}
                    label={`Proposed ${fields[field]?.label}`}
                    placeholder="Leave Empty if no proposal"
                    disabled={fields[field]?.isDisabled}
                    register={register(
                      `proposed.${field}`,
                      buildValidators(fields?.[field]?.validation)
                    )}
                    errorText={getErrorText(field)}
                  />
                </fieldset>
              </div>
            );
          })}
        </div>
      )}

      <fieldset className="w-full mt-5 pt-5 border-t">
        <CalculateEMI
          isValid={isValid}
          loanOffer={loanOffer}
          getValues={getValues}
        />

        <Input
          type="text"
          multiple={true}
          name={textArea?.name}
          label={textArea?.label}
          register={register(textArea?.name, {})}
          placeholder="comments if you have any suggestions that can improve the Proposal."
        />
      </fieldset>
    </>
  );
}
